import React, { FunctionComponent, Suspense, useState } from 'react';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay/hooks';
import { CatalogListLoader } from 'src/components/widgets/catalogList';
import { CustomErrorBoundary } from 'src/components/widgets/customErrorBoundary';
import { Section } from 'src/components/widgets/section';
import { showLoadingAction } from 'src/shared/actions';
import { useAppState } from 'src/shared/appState';
import { ErrorMessage } from 'src/shared/globals';
import { getMessageFromGraphQLError, T } from 'src/shared/helpers';
import { RelayFragment } from 'src/shared/types';
import styled from 'styled-components';
import { FormControl } from '../../../../widgets/formControl';
import { SearchInput } from '../../../../widgets/input';
import { ContainerBase } from '../../../../widgets/layouts';
import { showNotification } from '../../../../widgets/notificationToaster';
import { selectProviderProductAction, useProductConfigurationChangeStep, useProductConfigurationState } from '../../state';
import { productProvider_listItem$data } from '../../state/queries/__generated__/productProvider_listItem.graphql';
import ImportCatalogListQuery from './importCatalogList';
import SearchCategory from './searchCategory';

const SearchContainer = styled.div<{ hasCategory: boolean }>`
    display: grid;
    grid-gap: 30px;

    ${(props) =>
        props.hasCategory &&
        `
        grid-template-columns: 1fr 426px;
    `}

    ${(props) =>
        !props.hasCategory &&
        `
        grid-template-columns: 1fr;
    `}
`;

const ImportCatalog: FunctionComponent<{}> = (props) => {
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState<string | null>(null);
    const { state, dispatch } = useProductConfigurationState();
    const { dispatch: appDispatch } = useAppState();
    const changeStep = useProductConfigurationChangeStep();
    const relayEnvironment = useRelayEnvironment();

    const handleSearch = (search: string) => {
        setSearch(search);
    };

    const handleProductClick = async (providerProduct: Omit<RelayFragment<productProvider_listItem$data>, ' $fragmentType'>) => {
        // Ensure the product is compatible with Zakeke

        try {
            appDispatch(showLoadingAction(true));

            const query = await fetchQuery(
                relayEnvironment,
                graphql`
                    query importCatalogCheckQuery($productID: String!, $provider: ProductProvider!) {
                        providerProduct(productID: $productID, provider: $provider) {
                            ...productProvider_product
                        }
                    }
                `,
                {
                    productID: providerProduct.id,
                    provider: state.provider
                }
            ).toPromise();

            dispatch(selectProviderProductAction(providerProduct)); // Select merchant product
            changeStep('import-configuration'); // Go to next page
        } catch (ex: any) {
            let message = getMessageFromGraphQLError(ex);

            if (message === 'Product not supported.') message = T._('Product is not supported by Zakeke, please choose another product.', 'Admin');

            showNotification({
                message,
                type: 'danger',
                dismiss: {
                    duration: 5000
                }
            });
        } finally {
            appDispatch(showLoadingAction(false));
        }
    };

    return (
        <ContainerBase>
            <Section>
                <CustomErrorBoundary render={(error) => <ErrorMessage>{error?.message}</ErrorMessage>}>
                    <FormControl>
                        <SearchContainer hasCategory={state.provider !== 'CUSTOM_CAT'}>
                            <SearchInput fullWidth placeholder={T._('Search by product name ...', 'Admin')} onSearch={handleSearch} />

                            {state.provider !== 'CUSTOM_CAT' && (
                                <Suspense fallback={null}>
                                    <SearchCategory provider={state.provider} value={category} onChange={(id) => setCategory(id)} />
                                </Suspense>
                            )}
                        </SearchContainer>
                    </FormControl>

                    <Suspense fallback={CatalogListLoader}>
                        <ImportCatalogListQuery search={search} category={category} onProductClick={handleProductClick} />
                    </Suspense>
                </CustomErrorBoundary>
            </Section>
        </ContainerBase>
    );
};

export default ImportCatalog;
